import React from 'react';
import { Link } from 'react-router-dom';
import { Product } from '../types';
import { useCart } from '../context/CartContext';
import { formatCurrency } from '../utils';

interface ProductCardProps {
  product: Product;
  onAddToCart?: (productId: number) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onAddToCart }) => {
  const { addItemToCart } = useCart();

  const handleAddToCart = async () => {
    if (onAddToCart) {
      onAddToCart(product.id);
    } else {
      try {
        await addItemToCart(product.id.toString(), 1);
        // Trigger cart icon shake animation
        const cartIcon = document.getElementById('cart-icon');
        if (cartIcon) {
          cartIcon.classList.add('cart-shake');
          setTimeout(() => {
            cartIcon.classList.remove('cart-shake');
          }, 700);
        }
      } catch (error) {
        console.error('Error al agregar al carrito:', error);
      }
    }
  };

  const hasDiscount = (product.discount_percentage && product.discount_percentage > 0) || (product.discount_price && product.discount_price < product.price);
  const currentPrice = product.discount_price || product.price;

  return (
    <Link to={`/tienda/${product.slug}`} className="h-full w-full block">
      {/* Vista Desktop */}
      <div className="hidden md:flex w-full h-full flex-col text-center relative group bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300">
        {/* 1. Imagen */}
        <div className="relative overflow-hidden aspect-square">
          {hasDiscount && (
            <div className="absolute top-2 left-2 bg-black text-white text-xs font-bold px-3 py-1 z-10 rounded">
              -{product.discount_percentage}%
            </div>
          )}
          <img
            src={
              product.image ||
              (product as any).images?.[0]?.image ||
              `https://trae-api-us.mchost.guru/api/ide/v1/text_to_image?prompt=${encodeURIComponent(
                product.name
              )}%20beauty%20product%2C%20elegant%20packaging%2C%20professional%20product%20photography&image_size=square`
            }
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-500 ease-in-out group-hover:scale-110"
          />
        </div>

        {/* 2. Contenedor de Texto (este se estira) */}
        <div className="flex flex-col flex-grow p-4">
          {/* Info Superior (título, precio, stock) */}
          <h3 className="font-serif font-bold text-gray-900 mb-2 line-clamp-2 min-h-[3em]">
            {product.name}
          </h3>
          <div className="mb-2">
            {hasDiscount ? (
              <div className="flex items-center justify-center gap-2">
                <span className="font-sans font-bold text-red-600">
                  {formatCurrency(Number(currentPrice))}
                </span>
                <span className="font-sans text-sm text-gray-500 line-through">
                  {formatCurrency(Number(product.price))}
                </span>
              </div>
            ) : (
              <p className="font-sans font-semibold text-gray-900">
                {formatCurrency(Number(product.price))}
              </p>
            )}
          </div>
            <p className="font-sans text-sm text-gray-500 mb-4">
              {(product.total_stock ?? product.stock) > 0 
                ? `${product.total_stock ?? product.stock} disponibles` 
                : 'Agotado'}
            </p>
          
          {/* Espaciador para empujar el botón al fondo */}
          <div className="flex-grow"></div>

          {/* Botón */}
          <div className="pt-2">
            {(product.total_stock ?? product.stock) > 0 ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // Navegación manejada por el Link padre
                }}
                className="bg-primary-accent text-white rounded-none text-sm px-4 py-2 w-full hover:bg-primary-accent/90 transition-colors"
              >
                Ver Detalles
              </button>
            ) : (
              <button
                disabled
                className="bg-gray-300 text-gray-500 rounded-none text-sm px-4 py-2 w-full cursor-not-allowed"
              >
                Sin Stock
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Vista Móvil - Compacta */}
      <div className="md:hidden w-full relative group">
        {/* Imagen con texto superpuesto */}
        <div className="relative overflow-hidden">
          {hasDiscount && (
            <div className="absolute top-2 left-2 bg-black text-white text-xs font-bold px-2 py-1 z-10 rounded">
              -{product.discount_percentage}%
            </div>
          )}
          <div className="aspect-square bg-white flex items-center justify-center p-4">
            <img
              src={
                product.image ||
                (product as any).images?.[0]?.image ||
                `https://trae-api-us.mchost.guru/api/ide/v1/text_to_image?prompt=${encodeURIComponent(
                  product.name
                )}%20beauty%20product%2C%20elegant%20packaging%2C%20professional%20product%20photography&image_size=square`
              }
              alt={product.name}
              className="max-w-full max-h-full object-contain"
            />
          </div>
          
          {/* Overlay con información del producto */}
          <div className="bg-white p-3 border-t border-gray-100">
            <h3 className="font-serif font-bold text-gray-900 text-sm line-clamp-2 mb-1">
              {product.name}
            </h3>
            <div className="flex items-center justify-between">
              <div>
                {hasDiscount ? (
                  <div className="flex items-center gap-2">
                    <span className="font-sans font-bold text-red-600 text-sm">
                      {formatCurrency(Number(currentPrice))}
                    </span>
                    <span className="font-sans text-xs text-gray-500 line-through">
                      {formatCurrency(Number(product.price))}
                    </span>
                  </div>
                ) : (
                  <p className="font-sans font-semibold text-gray-900 text-sm">
                    {formatCurrency(Number(product.price))}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;